import React, { useEffect, useState } from 'react';
import { globalHistory, navigate } from '@reach/router';
import Modal from 'components/Core/Modal';
import useBreakpointView from 'utils/useBreakpointView';
import { stateList } from 'components/Form/utils/generalFormData';
import Button from 'components/Core/Button';
import filterIcon from 'images/filter-icon.svg';
import filterIconBlue from 'images/filter-icon-blue.svg';
import GetDemo from 'components/CallToActions/GetDemo';
import closeIcon from 'images/close-icon.png';
import { DownloadableContentFiltersContainer, FiltersContainer, FilterSelect } from './styles';
import { jobStageData } from '../static/data';

const DownloadableContentFilters = ({
  handleFilterUpdate,
  handleJobStageUpdate,
  industry,
  jobStage,
  isTypeSplash,
  posts,
  title,
  type,
  pageInfo,
  pathname,
}) => {
  const isMobile = useBreakpointView(['xs', 'sm']);
  const [selectedIndustry, setSelectedIndustry] = useState(industry);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedJobStage, setSelectedJobStage] = useState(null);
  const [stateOptions, setStateOptions] = useState(null);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const isLicensingSplash = (pathname || globalHistory.location.pathname)?.match(
    /([/])licensing([/])?$/,
  );
  const isLicensingIndustrySplash = (pathname || globalHistory.location.pathname)?.match(
    /([/])licensing([/]).+/,
  );
  const isTemplatesSplash = type === 'Templates';

  useEffect(() => {
    const states = posts
      .filter((post) => {
        const postIndustry = post.seo?.taxonomy?.industries?.[0];

        return post.downloadableContentType === type && postIndustry === selectedIndustry;
      })
      .reduce((acc, post) => {
        const { state } = post;
        if (state && !acc.includes(state)) {
          acc.push(state);
        }

        return acc;
      }, [])
      .map((stateAbbr) => {
        const value = stateList.find((state) => state.code === stateAbbr)?.value?.toLowerCase();
        return { label: stateAbbr, value };
      })
      .sort((b, a) => (a.label < b.label ? 1 : a.label > b.label ? -1 : 0));

    setStateOptions([...states]);
  }, [selectedIndustry]);

  useEffect(() => {
    setSelectedJobStage(jobStage);
  }, [jobStage]);

  useEffect(() => {
    const driftChatElems = document.querySelectorAll('.drift-conductor-item');

    if (mobileFilterOpen && isMobile) {
      setSelectedJobStage(jobStage);
      setSelectedIndustry(industry || (type === 'Templates' ? 'All Industries' : undefined));

      driftChatElems.forEach((ele) => (ele.style.display = 'none'));
    } else if (!mobileFilterOpen && isMobile) {
      driftChatElems.forEach((ele) => (ele.style.display = 'block'));
    }
  }, [mobileFilterOpen]);

  const formatValue = (value) =>
    value === 'Electrical' ? 'Electrician' : value.replace(/\s+/g, '-');

  const handleFilterChange = (filterType, value) => {
    const formattedValue = formatValue(value);
    if (handleFilterUpdate && filterType === 'industry' && !isMobile) {
      handleFilterUpdate(formattedValue, filterType);
    } else if (filterType === 'industry') {
      setSelectedIndustry(value);
    } else if (filterType === 'state') {
      setSelectedState(value);
    } else if (filterType === 'jobStage') {
      if (isMobile) {
        setSelectedJobStage(value);
      } else if (handleJobStageUpdate) {
        handleJobStageUpdate(value);
      }
    }
  };

  useEffect(() => {
    const options = posts
      .filter((post) => post.downloadableContentType === type)
      .reduce((acc, post) => {
        const industryItem = post?.seo?.taxonomy?.industries?.[0];

        if (industryItem && !acc.includes(industryItem)) {
          acc.push(industryItem);
        }

        return acc;
      }, [])
      .map((industryItem) => ({ label: industryItem, value: industryItem }))
      .sort((b, a) => (a.label < b.label ? 1 : a.label > b.label ? -1 : 0));

    const includesAllIndustries = options.some((option) => option.value === 'All Industries');

    if (!includesAllIndustries && type === 'Templates') {
      options.unshift({ label: 'All Industries', value: 'All Industries' });
    }

    setIndustryOptions(options);
  }, []);

  const formatPath = (path) => path.replace(/\s/g, '-').toLowerCase();

  const toggleMobileFilters = () => {
    setMobileFilterOpen(!mobileFilterOpen);
  };

  const handleButtonClick = () => {
    if (selectedIndustry) {
      const formattedIndustry = formatValue(selectedIndustry);
      if (isLicensingIndustrySplash && !isTypeSplash) {
        navigate(`/licensing/${formatPath(formattedIndustry)}/${formatPath(selectedState)}`);
      } else {
        navigate(
          `/${formatPath(type)}/${formatPath(
            formattedIndustry === 'All-Industries' ? '' : formattedIndustry,
          )}`,
          {
            state: { jobStage: selectedJobStage },
          },
        );
        handleJobStageUpdate(selectedJobStage);
      }
    } else if (isTemplatesSplash) {
      navigate(`/${formatPath(type)}`);
      handleJobStageUpdate(selectedJobStage);
    }

    toggleMobileFilters();
  };

  const handleCloseMobileFilter = () => {
    setSelectedJobStage(jobStage);
    setSelectedIndustry(industry || (type === 'Templates' ? 'All Industries' : undefined));
    setSelectedState(null);
    setMobileFilterOpen(false);
  };

  useEffect(() => {
    setSelectedIndustry(industry || (type === 'Templates' ? 'All Industries' : undefined));
  }, [industry]);

  const renderFilterContainer = () => (
    <FiltersContainer
      $isLicensingIndustrySplash={isLicensingIndustrySplash && !isTypeSplash}
      className={`${isTemplatesSplash || isLicensingSplash ? ' splash-filter' : ''}`}
    >
      {mobileFilterOpen && (
        <div className="download-splash-filter--mobile-header-container max-md">
          <h6 className="download-splash-filter--header-close" onClick={handleCloseMobileFilter}>
            <img
              src={closeIcon}
              className="download-splash-filter--header-close-icon"
              alt="close-icon"
            />
          </h6>
        </div>
      )}
      <div className="download-splash-filter--content">
        {title && <h5>{title}</h5>}
        {industryOptions && (
          <div className="download-splash-filter--item">
            <div className="download-splash-filter--name">
              <h6>Filter by Industry</h6>
            </div>

            <FilterSelect
              id="industry"
              options={industryOptions}
              value={selectedIndustry}
              handleChange={handleFilterChange}
              placeholder="Select industry..."
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </div>
        )}

        {isTemplatesSplash && (
          <div className="download-splash-filter--item">
            <div className="download-splash-filter--name">
              <h6>Filter by Job Stage</h6>
            </div>

            <FilterSelect
              id="jobStage"
              options={Object.keys(jobStageData).map((key) => ({
                label: jobStageData[key].label,
                value: key,
              }))}
              value={selectedJobStage}
              handleChange={handleFilterChange}
              placeholder="All Stages"
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </div>
        )}

        {stateOptions && !isTypeSplash && (
          <div className="download-splash-filter--item">
            <div className="download-splash-filter--name">
              <h6>Filter by State</h6>
            </div>

            <FilterSelect
              id="state"
              options={stateOptions}
              value={selectedState}
              handleChange={handleFilterChange}
              placeholder="Select state..."
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </div>
        )}

        <div
          className={`download-splash-filter--mobile-apply-filter ${
            isLicensingSplash
              ? isMobile
                ? ''
                : 'max-md'
              : stateOptions?.length > 0
              ? ''
              : 'max-md'
          }`}
        >
          <Button
            type="secondary"
            shadow={false}
            disabled={
              isTemplatesSplash
                ? !(selectedIndustry || selectedJobStage)
                : isLicensingIndustrySplash && !isTypeSplash
                ? !(selectedIndustry && selectedState)
                : !(selectedIndustry || selectedState)
            }
            onClick={handleButtonClick}
            className="search-button"
          >
            Search
          </Button>
        </div>
      </div>
    </FiltersContainer>
  );

  return (
    <DownloadableContentFiltersContainer className="downloadable-splash--filter">
      <div className="downloadable-splash--filter-content min-md">
        {renderFilterContainer()}
        <GetDemo url="/demo" />
      </div>
      <div className="downloadable-splash--filter-content max-md">
        <div className="download-posts-container--title-container">
          <p
            onClick={toggleMobileFilters}
            className={`download-posts-container--filter-btn ${
              ((selectedIndustry && selectedIndustry !== 'All Industries') ||
                selectedState ||
                (selectedJobStage && selectedJobStage !== 'all')) &&
              'selected'
            }`}
          >
            <img
              src={
                (selectedIndustry && selectedIndustry !== 'All Industries') ||
                selectedState ||
                (selectedJobStage && selectedJobStage !== 'all')
                  ? filterIconBlue
                  : filterIcon
              }
              className="download-posts-container--filter-icon"
              alt="filter-icon"
            />
            Filter
          </p>
          {!isLicensingIndustrySplash && (
            <p className="download-posts-container--post-count-mobile">{pageInfo}</p>
          )}
        </div>
        {isMobile && (
          <Modal
            className="downloadable-splash--filter-modal"
            open={mobileFilterOpen}
            hideCloseIcon
          >
            {renderFilterContainer()}
          </Modal>
        )}
      </div>
    </DownloadableContentFiltersContainer>
  );
};

export default DownloadableContentFilters;
